/*
 * TrackerMap: Canvas-based choropleth world map for project counts per country
 * - Parses XLSX dataset (via URL in data-dataset-url)
 * - Loads SVG world map (via URL in data-map-url) and converts paths to Path2D
 * - Renders responsive canvas with green-scale heatmap (#00DC6E variations)
 */

import * as XLSX from 'xlsx';
import AnnouncementChart from './announcement-chart';
import CustomScrollbar from './custom-scrollbar';

// Configuration constants
const CONFIG = {
    CANVAS_CACHE_ENABLED: true,
    HIT_TEST_THROTTLE_MS: 16, // ~60fps
    RESIZE_DEBOUNCE_MS: 150,

    LEGEND_COLORS: {
        MIN_COLOR: '#007D41',
        MAX_COLOR: '#00DC6E',
        ZERO_COLOR: '#D0D0D0',
        STROKE_COLOR: 'rgba(255,255,255,1)'
    },
    LAYOUT: {
        DEFAULT_GAP: 16,
        LARGE_SCREEN_GAP: 64,
        LARGE_SCREEN_BREAKPOINT: 1536
    }
};

const COUNTRY_NAME_ALIASES = new Map([
    ['UK', 'GB'],
    ['United Kingdom', 'GB'],
    ['Great Britain', 'GB'],
    ['Russia', 'RU'],
    ['South Korea', 'KR'],
    ['North Korea', 'KP'],
    ['Czechia', 'CZ'],
    ["Cote d'Ivoire", 'CI'],
    ['Côte d’Ivoire', 'CI'],
    ['DR Congo', 'CD'],
    ['Democratic Republic of the Congo', 'CD'],
    ['Republic of the Congo', 'CG'],
    ['Burma', 'MM'],
    ['Myanmar', 'MM'],
    ['Cape Verde', 'CV'],
    ['East Timor', 'TL'],
    ['Palestine', 'PS'],
    ['Taiwan', 'TW'],
    ['Venezuela', 'VE'],
    ['Vietnam', 'VN'],
    ['Syria', 'SY'],
    ['Tanzania', 'TZ'],
    ['Laos', 'LA'],
    ['USA', 'US'],
    ['United States', 'US'],
]);

export default class TrackerMap {
    constructor() {
        // Performance tracking
        this.performanceMetrics = { renderCount: 0, lastRenderTime: 0, avgRenderTime: 0 };

        // Canvas caching
        this.baseMapCanvas = null;
        this.baseMapCtx = null;
        this.baseMapDirty = true;

        // Event throttling
        this.mouseMoveThrottle = null;
        this.resizeDebounce = null;

        // Cleanup tracking
        this.eventListeners = [];
        this.animationFrameId = null;
        this.container = document.querySelector('.tracker-container');
        if (!this.container) return;

        this.datasetUrl = this.container.getAttribute('data-dataset-url') || '';
        this.mapUrl = this.container.getAttribute('data-map-url') || '';
        this.mappingUrl = this.container.getAttribute('data-mapping-url') || '';
        try {
            const memAttr = this.container.getAttribute('data-members') || '[]';
            this.memberNames = JSON.parse(memAttr);
        } catch (_) {
            this.memberNames = [];
        }

        // Debug logging flag: enable on localhost or when window.DEBUG_TRACKER === true
        this.debugEnabled = (typeof window !== 'undefined' && (
            window.DEBUG_TRACKER === true || (window.location && window.location.hostname === 'localhost')
        ));

        // Layout gap will be read from CSS to keep logic in styles
        this.layoutGap = 0;

        // Initialize base map canvas for caching
        this.initBaseMapCanvas();

        // Read current CSS gap from computed styles
        this.updateLayoutGap = () => {
            const styles = window.getComputedStyle(this.container);
            const raw = styles.columnGap || styles.gap || '0';
            const parsed = parseFloat(raw);
            this.layoutGap = Number.isFinite(parsed) ? parsed : 0;
        };
        this.updateLayoutGap();

        // Stats cards (rendered above the tracker maps; dataset-wide totals, not affected by filters)
        this.statsWrapper = document.createElement('div');
        this.statsWrapper.className = 'tracker-stats-wrapper';
        this.statsGrid = document.createElement('div');
        this.statsGrid.className = 'tracker-stats-grid';
        this.statsWrapper.appendChild(this.statsGrid);
        if (this.container.parentNode) {
            this.container.parentNode.insertBefore(this.statsWrapper, this.container);
        }

        // Loading overlay (appended to container so it's not affected by child element animations)
        this.loadingOverlay = document.createElement('div');
        this.loadingOverlay.className = 'tracker-loading-overlay';
        this.loadingOverlay.innerHTML = `
            <div class="tracker-loading-spinner"></div>
            <div class="tracker-loading-text">Loading tracker data...</div>
            <div class="tracker-loading-subtext">Please wait while we fetch the project information</div>
        `;
        this.container.appendChild(this.loadingOverlay);

        this.leftCol = document.createElement('div');
        this.leftCol.className = 'tracker-left';
        this.container.appendChild(this.leftCol);

        // Wrapper so legend can be positioned over the map
        this.mapWrap = document.createElement('div');
        this.mapWrap.className = 'tracker-map-wrap';
        this.leftCol.appendChild(this.mapWrap);

        // Filters (appear above the map)
        this.availableScales = new Set();
        this.availableTechnologies = new Set();
        this.availableStatuses = new Set();
        this.datasetMode = 'steel'; // 'steel' | 'cement'
        this.useSimpleSheets = false; // When true, trust first row headers and skip fallbacks
        this.steelUsesConsolidated = false;
        this.activeFilters = { scale: 'all', technology: 'all', status: 'all', type: 'both' };
        this.filtersBar = null;
        this.typeSelect = null;
        this.scaleSelect = null;
        this.techSelect = null;
        this.statusSelect = null;

        this.canvas = document.createElement('canvas');
        this.canvas.setAttribute('role', 'img');
        this.canvas.setAttribute('aria-label', 'World map showing project counts by country');
        this.ctx = this.canvas.getContext('2d');
        this.canvas.className = 'tracker-canvas';
        this.mapWrap.appendChild(this.canvas);

        this.tooltip = document.createElement('div');
        this.tooltip.className = 'tracker-tooltip';
        this.container.appendChild(this.tooltip);

        this.legend = document.createElement('div');
        this.legend.className = 'tracker-legend';
        this.mapWrap.appendChild(this.legend);

        // Sidebar to show clicked country's projects
        this.sidebar = document.createElement('aside');
        this.sidebar.className = 'tracker-sidebar';
        this.sidebar.setAttribute('aria-live', 'polite');
        this.container.appendChild(this.sidebar);

        // Initialize custom scrollbar for sidebar
        this.sidebarScrollbar = new CustomScrollbar(this.sidebar, { paddingY: 12 });

        // Chart container (placed after the main tracker container in DOM)
        this.chartWrapper = document.createElement('div');
        this.chartWrapper.className = 'tracker-chart-wrapper';
        // Insert chart wrapper after the tracker container
        this.container.parentNode.insertBefore(this.chartWrapper, this.container.nextSibling);
        this.announcementChart = null; // Will be initialized after dataset loads

        this.viewBox = { x: 0, y: 0, width: 1001, height: 545.28009 };
        this.isoToPaths = new Map(); // ISO2 -> Array<Path2D>
        this.isoToBBox = new Map(); // ISO2 -> {x,y,w,h}
        this.counts = new Map(); // ISO2 -> number
        this.projectsByIso = new Map(); // ISO2 -> Array<project> (filtered)
        this.allProjectsByIso = new Map(); // ISO2 -> Array<project> (unfiltered)
        this.iso2ToName = new Map(); // ISO2 -> Country Name
        this.hoverIso = null;
        this.selectedIso = null;
        this.hoveredRange = null; // {lower, upper} for legend range hover

        this.resizeObserver = new ResizeObserver(() => this.debouncedResize());
        this.resizeObserver.observe(this.container);

        // Add tracked event listeners
        this.addEventListenerTracked(this.canvas, 'mousemove', (e) => this.throttledMouseMove(e));
        this.addEventListenerTracked(this.canvas, 'mouseleave', () => this.hideTooltip());
        this.addEventListenerTracked(this.canvas, 'click', (e) => this.onClick(e));
        this.addEventListenerTracked(window, 'beforeunload', () => this.cleanup());

        this.init();
    }

    /**
     * Update sidebar height to match filters + map height on desktop
     */
    updateSidebarHeight(fallbackHeight) {
        if (!this.sidebar) return;
        const isDesktop = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 1024px)').matches;
        if (!isDesktop) {
            this.sidebar.style.height = '';
            this.sidebar.style.maxHeight = '';
            return;
        }

        const apply = () => {
            // Measure filters height if present
            const filtersHeight = this.filtersBar ? this.filtersBar.offsetHeight : 0;
            // Measure map wrapper height
            const mapHeight = this.mapWrap ? this.mapWrap.offsetHeight : (fallbackHeight || 0);
            // Account for vertical gap between filters and map in the left column
            let gapY = 0;
            if (this.leftCol) {
                const cs = window.getComputedStyle(this.leftCol);
                const rawGap = cs.gap || cs.rowGap || '0';
                const parsed = parseFloat(rawGap);
                gapY = Number.isFinite(parsed) ? parsed : 0;
            }
            const total = Math.ceil(filtersHeight + gapY + mapHeight);
            this.sidebar.style.height = total + 'px';
            this.sidebar.style.maxHeight = total + 'px';
            // Ensure vertical scrolling is enabled within the sidebar
            this.sidebar.style.overflowY = 'auto';
            this.sidebar.style.overflowX = 'hidden';
            // Prevent flex stretch from interfering
            this.sidebar.style.alignSelf = 'flex-start';
        };
        apply();
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
            window.requestAnimationFrame(apply);
        }
    }

    // Debug helpers
    debugLog(...args) {
        if (!this.debugEnabled) return;
        try { console.log(...args); } catch (_) { }
    }
    debugWarn(...args) {
        if (!this.debugEnabled) return;
        try { console.warn(...args); } catch (_) { }
    }

    async init() {
        try {
            // Load mapping and SVG in parallel (they're independent)
            await Promise.all([
                this.loadMapping(),
                this.loadSvgMap()
            ]);
            await this.loadDataset();
            this.updateStatsCards();
            this.setupFiltersBar();
            this.initAnnouncementChart();
            this.applyFilters();
            this.buildLegend();
            this.resize();

            // Animate elements in with staggered timing
            this.animateIn();
        } catch (error) {
            console.warn('TrackerMap initialization failed:', error);
            this.showErrorState('Failed to load map data');

            // Hide loading overlay on error as well
            if (this.loadingOverlay) {
                this.loadingOverlay.classList.add('fade-out');
                setTimeout(() => {
                    this.loadingOverlay.classList.add('hidden');
                }, 400);
            }
        }
    }

    animateIn() {
        // Fade out loading overlay first
        if (this.loadingOverlay) {
            this.loadingOverlay.classList.add('fade-out');
            setTimeout(() => {
                this.loadingOverlay.classList.add('hidden');
            }, 400);
        }

        // Stagger the appearance of elements for a smooth cascading effect
        const timings = [
            { element: this.statsWrapper, delay: 50 },
            { element: this.filtersBar, delay: 150 },
            { element: this.mapWrap, delay: 250 },
            { element: this.sidebar, delay: 350 },
            { element: this.chartWrapper, delay: 500 }
        ];

        timings.forEach(({ element, delay }) => {
            if (element) {
                setTimeout(() => {
                    element.classList.add('visible');
                }, delay);
            }
        });
    }

    /**
     * Render dataset-wide stats (ignores filters)
     */
    updateStatsCards() {
        if (!this.statsGrid) return;
        const allProjects = [];
        for (const projects of this.allProjectsByIso.values()) {
            allProjects.push(...projects);
        }

        const totalProjects = allProjects.length;
        const totalInvestmentMUsd = allProjects.reduce((sum, p) => {
            const v = Number(p.investmentMUsd);
            return sum + (Number.isFinite(v) ? v : 0);
        }, 0);
        const totalCaptureMtCO2 = allProjects.reduce((sum, p) => {
            const v = Number(p.expectedCaptureMtCO2);
            return sum + (Number.isFinite(v) ? v : 0);
        }, 0);
        const totalHydrogenMW = allProjects.reduce((sum, p) => {
            const v = Number(p.hydrogenCapacityMW);
            return sum + (Number.isFinite(v) ? v : 0);
        }, 0);

        const fmtInt = (n) => Number(n || 0).toLocaleString();
        const fmtMUsd = (n) => {
            const v = Number(n || 0);
            const rounded = Math.round(v);
            return `$${rounded.toLocaleString()}m`;
        };
        const fmtCapture = (n) => {
            const val = Number(n || 0);
            return `${val.toFixed(1)} Mt/year`;
        };
        const fmtHydrogen = (n) => {
            return `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} MW`;
        };

        const prefersReducedMotion = (typeof window !== 'undefined'
            && window.matchMedia
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

        const animateNumber = (el, from, to, {
            durationMs = 1400,
            splitT = 0.65,      // portion of time for the “fast” phase
            splitV = 0.92,      // portion of value reached at end of fast phase
            formatter = (x) => String(x),
        } = {}) => {
            if (!el) return;
            const start = performance.now();
            const delta = to - from;
            const easeOut = (u) => 1 - Math.pow(1 - u, 3); // cubic (we slow the tail via splitT/splitV)
            let lastShown = null;
            let lastChangeAt = start;
            const step = (now) => {
                const t = Math.min(1, (now - start) / durationMs);
                // Two-phase easing: reach ~splitV quickly, then spend more time creeping to 100%.
                const eased = (t < splitT)
                    ? (easeOut(t / splitT) * splitV)
                    : (splitV + easeOut((t - splitT) / (1 - splitT)) * (1 - splitV));
                const current = from + delta * eased;

                // Make the *time between visible number changes* longer near the end.
                // Base interval is snappy; end interval is noticeably slower.
                const minIntervalMs = 20 + Math.round(220 * Math.pow(t, 3)); // ~20ms -> ~240ms
                const currentDisplay = Math.round(current);

                if (t >= 1) {
                    el.textContent = formatter(to);
                    lastShown = to;
                    return;
                }

                if (currentDisplay !== lastShown && (now - lastChangeAt) >= minIntervalMs) {
                    el.textContent = formatter(currentDisplay);
                    lastShown = currentDisplay;
                    lastChangeAt = now;
                }
                if (t < 1) {
                    this.animationFrameId = requestAnimationFrame(step);
                }
            };
            el.textContent = formatter(from);
            lastShown = from;
            this.animationFrameId = requestAnimationFrame(step);
        };

        const cards = [
            { label: 'Total projects', value: fmtInt(totalProjects) },
            { label: 'Total invested', value: fmtMUsd(totalInvestmentMUsd) },
            { label: 'CO₂ capture capacity', value: fmtCapture(totalCaptureMtCO2) },
            { label: 'Hydrogen capacity', value: fmtHydrogen(totalHydrogenMW) },
        ];

        this.statsGrid.innerHTML = '';
        cards.forEach((c) => {
            const card = document.createElement('div');
            card.className = 'tracker-stat-card';
            const label = document.createElement('div');
            label.className = 'tracker-stat-label';
            label.textContent = c.label;
            const value = document.createElement('div');
            value.className = 'tracker-stat-value';
            value.textContent = c.value;
            card.appendChild(label);
            card.appendChild(value);
            if (c.meta) {
                const meta = document.createElement('div');
                meta.className = 'tracker-stat-meta';
                meta.textContent = c.meta;
                card.appendChild(meta);
            }
            this.statsGrid.appendChild(card);

            if (prefersReducedMotion) return;

            // Animate in the number (from 0 → target). Keep raw numbers separate from formatted strings.
            if (c.label === 'Total projects') {
                animateNumber(value, 0, totalProjects, {
                    durationMs: 1600,
                    formatter: (x) => fmtInt(Math.round(x)),
                });
            } else if (c.label === 'Total invested') {
                animateNumber(value, 0, totalInvestmentMUsd, {
                    durationMs: 1900,
                    formatter: (x) => fmtMUsd(Math.round(x)),
                });
            } else if (c.label === 'CO₂ capture capacity') {
                animateNumber(value, 0, totalCaptureMtCO2, {
                    durationMs: 1800,
                    formatter: (x) => `${x.toFixed(1)} Mt/year`,
                });
            } else if (c.label === 'Hydrogen capacity') {
                animateNumber(value, 0, totalHydrogenMW, {
                    durationMs: 1700,
                    formatter: (x) => `${Math.round(x).toLocaleString()} MW`,
                });
            }
        });
    }

    /**
     * Initialize the announcement timeline chart
     */
    initAnnouncementChart() {
        if (!this.chartWrapper) return;
        this.announcementChart = new AnnouncementChart(this.chartWrapper, this.datasetMode);
    }

    /**
     * Add tracked event listeners for cleanup
     */
    addEventListenerTracked(element, event, handler) {
        element.addEventListener(event, handler);
        this.eventListeners.push({ element, event, handler });
    }

    /**
     * Throttled mouse move handler
     */
    throttledMouseMove(e) {
        if (this.mouseMoveThrottle) return;

        this.mouseMoveThrottle = setTimeout(() => {
            this.onMouseMove(e);
            this.mouseMoveThrottle = null;
        }, CONFIG.HIT_TEST_THROTTLE_MS);
    }

    /**
     * Debounced resize handler
     */
    debouncedResize() {
        if (this.resizeDebounce) clearTimeout(this.resizeDebounce);

        this.resizeDebounce = setTimeout(() => {
            this.resize();
            this.resizeDebounce = null;
        }, CONFIG.RESIZE_DEBOUNCE_MS);
    }

    /**
     * Initialize base map canvas for caching
     */
    initBaseMapCanvas() {
        if (!CONFIG.CANVAS_CACHE_ENABLED) return;

        this.baseMapCanvas = document.createElement('canvas');
        this.baseMapCtx = this.baseMapCanvas.getContext('2d');
        this.baseMapDirty = true;
    }

    /**
     * Render base map to cache canvas
     */
    renderBaseMapToCache() {
        if (!CONFIG.CANVAS_CACHE_ENABLED || !this.baseMapCtx || !this.baseMapDirty) return;

        // Set cache canvas size to match main canvas
        this.baseMapCanvas.width = this.canvas.width;
        this.baseMapCanvas.height = this.canvas.height;

        // Apply same transform
        this.baseMapCtx.setTransform(
            this.scaleX, 0, 0, this.scaleY,
            -this.viewBox.x * this.scaleX,
            -this.viewBox.y * this.scaleY
        );

        const { colorFor, stroke } = this.getColorScale();

        // Clear and render base map
        this.baseMapCtx.save();
        this.baseMapCtx.setTransform(1, 0, 0, 1, 0, 0);
        this.baseMapCtx.clearRect(0, 0, this.baseMapCanvas.width, this.baseMapCanvas.height);
        this.baseMapCtx.fillStyle = '#FFFFFF';
        this.baseMapCtx.fillRect(0, 0, this.baseMapCanvas.width, this.baseMapCanvas.height);
        this.baseMapCtx.restore();

        // Draw countries
        this.isoToPaths.forEach((paths, iso2) => {
            const val = this.counts.get(iso2) || 0;
            const fill = colorFor(val);
            this.baseMapCtx.fillStyle = fill;
            this.baseMapCtx.strokeStyle = stroke;
            this.baseMapCtx.lineWidth = 1.0;
            for (const p of paths) {
                this.baseMapCtx.beginPath();
                this.baseMapCtx.fill(p);
                this.baseMapCtx.stroke(p);
            }
        });

        this.baseMapDirty = false;
    }

    /**
     * Show error state
     */
    showErrorState(message) {
        if (!this.container) return;

        this.container.innerHTML = `
            <div style="
                display: flex;
                align-items: center;
                justify-content: center;
                height: 400px;
                background: #f8f9fa;
                border-radius: 8px;
                color: #6c757d;
                font-family: system-ui, sans-serif;
            ">
                <div style="text-align: center;">
                    <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
                    <div style="font-size: 18px; font-weight: 500;">${message}</div>
                </div>
            </div>
        `;
    }

    /**
     * Cleanup resources
     */
    cleanup() {
        // Clear timeouts
        if (this.mouseMoveThrottle) clearTimeout(this.mouseMoveThrottle);
        if (this.resizeDebounce) clearTimeout(this.resizeDebounce);
        if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);

        // Remove event listeners
        this.eventListeners.forEach(({ element, event, handler }) => {
            element.removeEventListener(event, handler);
        });
        this.eventListeners = [];

        // Disconnect observers
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        // Clear maps to free memory
        this.isoToPaths.clear();
        this.counts.clear();
        this.projectsByIso.clear();
        this.allProjectsByIso.clear();
        this.iso2ToName.clear();

        // Cleanup announcement chart
        if (this.announcementChart) {
            this.announcementChart.destroy();
            this.announcementChart = null;
        }

        // Cleanup sidebar scrollbar
        if (this.sidebarScrollbar) {
            this.sidebarScrollbar.destroy();
            this.sidebarScrollbar = null;
        }
    }


    async loadSvgMap() {
        if (!this.mapUrl) return;
        const res = await fetch(this.mapUrl);
        const svgText = await res.text();
        const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
        const svgEl = doc.querySelector('svg');
        if (svgEl && svgEl.getAttribute('viewBox')) {
            const [x, y, w, h] = svgEl.getAttribute('viewBox').split(/\s+/).map(parseFloat);
            this.viewBox = { x, y, width: w, height: h };
        }


        // Countries may be <path id="xx"> or <g id="xx"> containing paths.
        // Filter out tiny non-country artifacts by requiring id length 2 or common territories
        const countryNodes = svgEl.querySelectorAll('path[id], g[id]');
        countryNodes.forEach((node) => {
            const id = node.getAttribute('id');
            if (!id) return;
            const lower = id.toLowerCase();
            // Try mapping.csv first, else derive from id if exactly 2 letters
            let iso2 = this.svgIdToIso2 && this.svgIdToIso2.get(lower);
            if (!iso2) {
                if (lower.startsWith('_')) return; // skip special regions without ISO
                if (lower.length !== 2) return;
                iso2 = lower.toUpperCase();
            }
            const paths = [];
            if (node.tagName.toLowerCase() === 'path') {
                const d = node.getAttribute('d');
                if (d) paths.push(new Path2D(d));
            } else {
                node.querySelectorAll('path').forEach((p) => {
                    const d = p.getAttribute('d');
                    if (d) paths.push(new Path2D(d));
                });
            }
            if (paths.length > 0) {
                this.isoToPaths.set(iso2, paths);
            }
        });
    }

    async loadMapping() {
        if (!this.mappingUrl) return;
        try {
            const res = await fetch(this.mappingUrl);
            const text = await res.text();
            const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
            // Skip header
            this.svgIdToIso2 = new Map();
            this.nameToIso2 = new Map();
            for (let i = 1; i < lines.length; i += 1) {
                const line = lines[i];
                const m = line.match(/^\s*"([^"]+)"\s*,\s*"([^"]*)"\s*$/);
                if (!m) continue;
                const svgId = m[1].toLowerCase();
                const countryName = m[2];
                // Map svg id to ISO2 where possible (assume svg ids are ISO2 lowercase except for specials)
                if (svgId.length === 2) {
                    const iso2 = svgId.toUpperCase();
                    this.svgIdToIso2.set(svgId, iso2);
                    if (countryName) this.iso2ToName.set(iso2, countryName);
                    const n1 = normalizeName(countryName);
                    if (n1) this.nameToIso2.set(n1, iso2);
                }
            }
            // Common extended names → ISO2
            const extra = [
                ['united states of america', 'US'],
                ['u.s.', 'US'],
                ['u.s.a', 'US'],
                ['russia', 'RU'],
                ['republic of korea', 'KR'],
                ['south korea', 'KR'],
                ['north korea', 'KP'],
                ['czech republic', 'CZ'],
                ['cote d’ivoire', 'CI'],
                ["cote d'ivoire", 'CI'],
                ['democratic republic of the congo', 'CD'],
                ['republic of the congo', 'CG'],
                ['myanmar (burma)', 'MM'],
                ['burma', 'MM'],
                ['timor-leste', 'TL'],
                ['palestine', 'PS'],
                ['viet nam', 'VN'],
            ];
            for (const [k, v] of extra) this.nameToIso2.set(k, v);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('Failed to load mapping.csv', e);
        }

        // Rebuild isoToPaths using mapping if we already parsed the SVG
        // If isoToPaths is empty, it will be filled when loadSvgMap runs
    }

    async loadDataset() {
        if (!this.datasetUrl) return;
        try {
            const res = await fetch(this.datasetUrl);
            const arrayBuf = await res.arrayBuffer();
            const wb = XLSX.read(arrayBuf);
            const detectHeaderRowIndex = (ws) => {
                try {
                    const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
                    const maxScan = Math.min(15, grid.length);
                    const hasKey = (row, needle) => row.some((cell) => String(cell || '').trim().toLowerCase() === needle);
                    const hasKeyLoose = (row, needle) => row.some((cell) => String(cell || '').trim().toLowerCase().includes(needle));
                    for (let i = 0; i < maxScan; i += 1) {
                        const row = Array.isArray(grid[i]) ? grid[i] : [];
                        // We expect at least "Project name" + "Country" to exist on the real header row
                        const hasProjectName = hasKey(row, 'project name') || hasKeyLoose(row, 'project name');
                        const hasCountry = hasKey(row, 'country') || hasKeyLoose(row, 'country');
                        if (hasProjectName && hasCountry) return i;
                    }
                } catch (_) {
                    // ignore and fall back to default parsing
                }
                return null;
            };
            // Determine dataset mode and sheets to parse
            const toKey = (s) => String(s || '').trim().toLowerCase();
            // const namesLower = wb.SheetNames.map((n) => toKey(n)); // unused
            // Preferred exact sheet names
            const ccExact = wb.SheetNames.filter((n) => toKey(n).startsWith('1. cc projects'));
            const kilnExact = wb.SheetNames.filter((n) => toKey(n).startsWith('2. clay calcination kilns'));
            const consolidateExact = wb.SheetNames.filter((n) => toKey(n).startsWith('1.2 consolidate'));
            // Looser matches as fallback
            const ccNames = ccExact.length ? ccExact : wb.SheetNames.filter((n) => toKey(n).includes('cc projects'));
            const kilnNames = kilnExact.length ? kilnExact : wb.SheetNames.filter((n) => {
                const ln = toKey(n);
                return ln.includes('calcination') || ln.includes('clay calcination');
            });
            const sheetsToParse = [];
            if (ccNames.length || kilnNames.length) {
                // Cement dataset detected
                this.datasetMode = 'cement';
                this.useSimpleSheets = true;
                ccNames.forEach((n) => sheetsToParse.push({ name: n, typeNormalized: 'cc', typeLabel: 'CC Projects' }));
                kilnNames.forEach((n) => sheetsToParse.push({ name: n, typeNormalized: 'kilns', typeLabel: 'Calcination Kilns' }));
            } else {
                // Steel dataset: Look for new sheet structure (2024+ format)

                // "2. Qualified Projects" (pre-filtered, header at row 3)
                const qualifiedSheets = wb.SheetNames.filter((n) => {
                    const ln = toKey(n);
                    return /^\d+\.\s*qualified\s+projects?$/i.test(ln);
                });

                // "3. All Projects" (needs filtering, header at row 5)
                const allProjectsSheets = wb.SheetNames.filter((n) => {
                    const ln = toKey(n);
                    return /^\d+\.\s*all\s+projects?$/i.test(ln);
                });

                // Priority order: Qualified Projects > All Projects
                if (qualifiedSheets.length) {
                    this.debugLog('[TrackerMap] Using "Qualified Projects" sheet');
                    sheetsToParse.push({
                        name: qualifiedSheets[0],
                        stage: 'qualified',
                        headerRow: 3, // Header is at row 3
                        filterQualified: false // Already filtered
                    });
                    this.datasetMode = 'steel';
                    this.useSimpleSheets = true;
                    this.steelUsesConsolidated = false;
                } else if (allProjectsSheets.length) {
                    this.debugLog('[TrackerMap] Using "All Projects" sheet with filtering');
                    sheetsToParse.push({
                        name: allProjectsSheets[0],
                        stage: 'all',
                        headerRow: 5, // Header is at row 5
                        filterQualified: true // Need to filter by "Qualified for Tracker" = "Yes"
                    });
                    this.datasetMode = 'steel';
                    this.useSimpleSheets = true;
                    this.steelUsesConsolidated = false;
                } else {
                    // No recognized sheets found
                    this.debugWarn('[TrackerMap] No recognized project sheets found in dataset');
                    this.debugWarn('[TrackerMap] Expected sheets: "2. Qualified Projects" or "3. All Projects"');
                    // Try first sheet as last resort
                    const fallback = wb.SheetNames[0];
                    if (fallback) {
                        this.debugWarn('[TrackerMap] Falling back to first sheet:', fallback);
                        sheetsToParse.push({ name: fallback, stage: 'active' });
                    }
                }
            }

            let matched = 0;
            const unmatchedSamples = [];
            const parseInvestmentMUsd = (raw) => {
                const s = String(raw || '').trim();
                if (!s) return 0;
                if (/^(n\/a|na|none|unknown|tbd|-|—)$/i.test(s)) return 0;
                const cleaned = s
                    .replace(/[,]/g, '')
                    .replace(/\$/g, '')
                    .replace(/[^\d.\-]/g, '')
                    .trim();
                const v = parseFloat(cleaned);
                return Number.isFinite(v) ? v : 0;
            };
            const parseCapacity = (raw) => {
                const s = String(raw || '').trim();
                if (!s) return 0;
                if (/^(n\/a|na|none|unknown|tbd|-|—)$/i.test(s)) return 0;
                const cleaned = s.replace(/[,]/g, '').replace(/[^\d.\-]/g, '');
                const v = parseFloat(cleaned);
                return Number.isFinite(v) ? v : 0;
            };
            // Pre-compile continent map (optimization: avoid creating Map per call)
            const CONTINENT_MAP = new Map([
                ['africa', 'Africa'],
                ['asia', 'Asia'],
                ['europe', 'Europe'],
                ['north america', 'North America'],
                ['n america', 'North America'],
                ['south america', 'South America'],
                ['s america', 'South America'],
                ['oceania', 'Oceania'],
                ['australia', 'Oceania'],
                ['australia/oceania', 'Oceania'],
            ]);
            const normalizeContinent = (raw) => {
                const s = String(raw || '').trim();
                if (!s) return 'Unknown';
                const key = s
                    .toLowerCase()
                    .replace(/\./g, '')
                    .replace(/\s+/g, ' ')
                    .trim();
                return CONTINENT_MAP.get(key) || s;
            };
            // Pre-compile regex patterns (optimization: avoid recompiling per row)
            const ISO_PATTERN = /(^|\b)iso(2)?($|\b)|(^|_)code($|_)/i;
            const COUNTRY_CODE_PATTERN = /(^|\b)country code($|\b)/i;
            const COUNTRY_PATTERN = /^country$/i;
            const COUNTRY_LOOSE_PATTERN = /(^|\b)(country|location|nation|state)(\b|$)/i;
            const CONTINENT_PATTERN = /^continent$/i;
            const CONTINENT_LOOSE_PATTERN = /continent/i;
            const CAPTURE_PATTERN = /capture.*capacity|CO2/i;
            const HYDROGEN_PATTERN = /hydrogen.*capacity/i;
            for (const sheetMeta of sheetsToParse) {
                const wsName = sheetMeta.name;

                // Skip dashboard and pivot sheets
                if (toKey(wsName).includes('dashboard') || toKey(wsName).includes('pivot')) {
                    continue;
                }

                const stage = sheetMeta.stage;
                const ws = wb.Sheets[wsName];
                // Use explicit header row from sheet metadata if provided (new format),
                // otherwise auto-detect (legacy format)
                const headerRowIdx = sheetMeta.headerRow != null
                    ? sheetMeta.headerRow - 1 // Convert to 0-based index for XLSX library
                    : detectHeaderRowIndex(ws);
                let rows = headerRowIdx != null
                    ? XLSX.utils.sheet_to_json(ws, { defval: '', range: headerRowIdx })
                    : XLSX.utils.sheet_to_json(ws, { defval: '' });

                // If using "All Projects" sheet in new format, filter by qualification status
                if (sheetMeta.filterQualified) {
                    const beforeFilter = rows.length;
                    rows = rows.filter((row) => {
                        // Check "Qualified for Tracker" column (handle various normalizations)
                        const qualified = row['Qualified for Tracker'] ||
                                        row['qualified_for_tracker'] ||
                                        row['Qualified for tracker'] ||
                                        row['qualified for tracker'];
                        return qualified === 'Yes' || qualified === 'yes' || qualified === 'YES';
                    });
                    const afterFilter = rows.length;
                    this.debugLog(`[TrackerMap] Filtered ${beforeFilter} projects → ${afterFilter} qualified projects`);
                }

                // Determine effective type for this sheet for logging below (cement)
                const effectiveType = (this.datasetMode === 'cement') ? (sheetMeta.typeNormalized || '') : '';
                if (this.datasetMode === 'cement' && effectiveType === 'cc') {
                    const headerKeys = rows.length ? Object.keys(rows[0]) : [];
                    this.debugLog('[TrackerMap][CC] Sheet detected:', wsName);
                    this.debugLog('[TrackerMap][CC] Header keys:', headerKeys);
                    this.debugLog('[TrackerMap][CC] Row count:', rows.length);
                }

                // Simple mode: trust first row headers; no header repair

                let debugLoggedUnmatched = 0;
                for (const row of rows) {
                    let debugCountrySource = '';
                    let debugRawCountry = '';
                    let iso2 = '';
                    // 1) Direct ISO-like code columns (using pre-compiled patterns)
                    let keyIso = Object.keys(row).find((k) => ISO_PATTERN.test(k));
                    if (!keyIso) {
                        keyIso = Object.keys(row).find((k) => COUNTRY_CODE_PATTERN.test(String(k)));
                    }
                    if (keyIso) {
                        const rawIso = String(row[keyIso]).trim();
                        if (/^[A-Za-z]{2}$/.test(rawIso)) iso2 = rawIso.toUpperCase();
                    }
                    // 2) Country name columns
                    if (!iso2) {
                        // Prefer explicit 'country', else parse continent/country/location, taking country
                        let rawCountry = '';
                        // Try direct key matches first (using pre-compiled patterns)
                        let countryKey = Object.keys(row).find((k) => COUNTRY_PATTERN.test(k.trim()));
                        if (!countryKey) countryKey = Object.keys(row).find((k) => COUNTRY_LOOSE_PATTERN.test(k.trim()));
                        if (countryKey) {
                            rawCountry = String(row[countryKey]).trim();
                            debugCountrySource = `direct:${countryKey}`;
                        }
                        // Try fallback: check for normalized variations
                        if (!rawCountry && !this.useSimpleSheets) {
                            // Try country_area or similar normalized keys
                            rawCountry = String(row['country_area'] || row['country'] || '').trim();
                            if (rawCountry) debugCountrySource = 'normalized:country|country_area';
                        }
                        if (rawCountry) {
                            let raw = rawCountry;
                            if (!raw) {
                                // nothing
                            } else {
                                // Remove text in parentheses and trailing commas
                                raw = raw.replace(/\([^)]*\)/g, ' ').replace(/\s+,/g, ',').trim();
                                // If it's like "City, Country" take last segment as country hint
                                let candidate = raw;
                                if (raw.includes(',')) candidate = raw.split(',').pop().trim();
                                const norm = normalizeName(candidate);
                                const aliased = COUNTRY_NAME_ALIASES.get(candidate) || COUNTRY_NAME_ALIASES.get(capitalize(candidate));
                                if (aliased) iso2 = aliased;
                                else if (this.nameToIso2 && this.nameToIso2.has(norm)) iso2 = this.nameToIso2.get(norm);
                                else if (/^[A-Z]{2}$/.test(candidate)) iso2 = candidate.toUpperCase();
                                debugRawCountry = rawCountry;
                            }
                        }
                    }
                    if (!iso2) {
                        if (this.datasetMode === 'cement' && effectiveType === 'cc' && debugLoggedUnmatched < 25) {
                            this.debugWarn('[TrackerMap][CC] Unmatched country row', {
                                sheet: wsName,
                                country: debugRawCountry,
                                countrySource: debugCountrySource,
                                keys: Object.keys(row),
                                sample: row
                            });
                            debugLoggedUnmatched += 1;
                        }
                        continue;
                    }
                    // Collect project details
                    const projectName = row.project_name || row['Project name'] || row.project || row.internal_id || '';
                    const technology = row.technology_to_be_used || row['Technology to be used'] || row.technology_category || '';
                    const scale = row.project_scale || row['Project scale'] || row['[ref] Project scale'] || row['Project scale [ref]'] || '';
                    const status = row.project_status || row['Project status'] || '';
                    const company = row.company || '';
                    const memberCompanies = this.extractMemberCompanies(company);
                    // Extract announcement date
                    const announcementDateRaw = row['Date of announcement (yyyy-mm-dd)'] || row['date_of_announcement'] || row['Date of announcement'] || '';
                    const announcementYear = parseAnnouncementYear(announcementDateRaw);
                    // Extract continent (used for per-continent announcement chart series)
                    let continentRaw = '';
                    let continentKey = Object.keys(row).find((k) => CONTINENT_PATTERN.test(String(k || '').trim()));
                    if (!continentKey) continentKey = Object.keys(row).find((k) => CONTINENT_LOOSE_PATTERN.test(String(k || '')));
                    if (continentKey) continentRaw = String(row[continentKey]).trim();
                    if (!continentRaw && !this.useSimpleSheets) {
                        continentRaw = String(getByHeader(row, ['continent'])).trim();
                    }
                    const continent = normalizeContinent(continentRaw);

                    // Extract investment (m USD). Column letters vary by dataset; prefer header name match.
                    const investmentKey = Object.keys(row).find((k) => /investment/i.test(String(k)) && /m\s*usd/i.test(String(k)));
                    const investmentRaw = investmentKey ? row[investmentKey] : (
                        row['Investment (m USD)'] ||
                        row.investment ||
                        ''
                    );
                    const investmentMUsd = parseInvestmentMUsd(investmentRaw);

                    // Extract CO2 capture capacity (million tonnes per year)
                    const captureKey = Object.keys(row).find((k) => CAPTURE_PATTERN.test(String(k)));
                    const captureRaw = captureKey ? row[captureKey] : (
                        row['Expected capture capacity (million tonnes CO2 per year)'] ||
                        row['expected_capture_capacity'] ||
                        ''
                    );
                    const expectedCaptureMtCO2 = parseCapacity(captureRaw);

                    // Extract hydrogen generation capacity (MW)
                    const hydrogenKey = Object.keys(row).find((k) => HYDROGEN_PATTERN.test(String(k)));
                    const hydrogenRaw = hydrogenKey ? row[hydrogenKey] : (
                        row['Hydrogen generation capacity (MW)'] ||
                        row['hydrogen_generation_capacity'] ||
                        ''
                    );
                    const hydrogenCapacityMW = parseCapacity(hydrogenRaw);

                    const project = { projectName, technology, scale, status, company, memberCompanies, stage, announcementYear, continent, investmentMUsd, expectedCaptureMtCO2, hydrogenCapacityMW };
                    if (this.datasetMode === 'cement') {
                        project.typeNormalized = sheetMeta.typeNormalized || 'cc';
                        project.typeLabel = sheetMeta.typeLabel || 'CC Projects';
                    }
                    if (!this.allProjectsByIso.has(iso2)) this.allProjectsByIso.set(iso2, []);
                    this.allProjectsByIso.get(iso2).push(project);
                    // Collect filter options
                    if (technology && String(technology).trim()) this.availableTechnologies.add(String(technology).trim());
                    if (scale && String(scale).trim()) this.availableScales.add(String(scale).trim());
                    if (status && String(status).trim()) this.availableStatuses.add(String(status).trim());
                    matched += 1;

                    // Debug log for CC rows
                    if (this.datasetMode === 'cement' && effectiveType === 'cc') {
                        this.debugLog('[TrackerMap][CC] Row', {
                            sheet: wsName,
                            iso2,
                            country: debugRawCountry,
                            countrySource: debugCountrySource,
                            projectName,
                            technology,
                            scale,
                            status,
                            stage,
                            company
                        });
                    }
                }
            }

            // No localStorage caching in simplified mode
            if (matched === 0) {
                this.debugWarn('[TrackerMap] Parsed 0 projects from dataset', {
                    datasetMode: this.datasetMode,
                    steelUsesConsolidated: this.steelUsesConsolidated,
                    sheetsParsed: sheetsToParse.map((s) => s.name),
                });
            }

        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('Failed to parse dataset', err);
        }
    }

    // No localStorage caching helpers in simplified version

    resize() {
        const dpr = window.devicePixelRatio || 1;
        // Keep layout gap in sync with current viewport
        if (this.updateLayoutGap) this.updateLayoutGap();
        // Use actual map wrapper width for canvas sizing. This naturally
        // excludes the sidebar on large screens and handles full-bleed on mobile.
        const widthSource = this.mapWrap && this.mapWrap.clientWidth ? this.mapWrap : this.container;
        const width = Math.max(320, widthSource.clientWidth);
        const aspect = this.viewBox.width / this.viewBox.height;
        const height = Math.round(width / aspect);

        // Check if size actually changed to avoid unnecessary work
        const newCanvasWidth = Math.round(width * dpr);
        const newCanvasHeight = Math.round(height * dpr);
        const sizeChanged = this.canvas.width !== newCanvasWidth || this.canvas.height !== newCanvasHeight;

        // CSS controls canvas display size; set only device-pixel buffer size
        this.canvas.width = newCanvasWidth;
        this.canvas.height = newCanvasHeight;

        // Sidebar height: on desktop, match filters+map (avoid flex stretch feedback)
        this.updateSidebarHeight(height);

        // Compute transform from SVG viewBox space -> canvas pixels
        this.scaleX = this.canvas.width / this.viewBox.width;
        this.scaleY = this.canvas.height / this.viewBox.height;
        // Apply scale and translate by -viewBox origin so content fits starting at 0,0
        this.ctx.setTransform(
            this.scaleX,
            0,
            0,
            this.scaleY,
            -this.viewBox.x * this.scaleX,
            -this.viewBox.y * this.scaleY,
        );

        // Mark base map as dirty if size changed
        if (sizeChanged) {
            this.baseMapDirty = true;
        }

        this.render();
    }

    setupFiltersBar() {
        if (this.filtersBar) return;
        this.filtersBar = document.createElement('div');
        this.filtersBar.className = 'tracker-filters';

        const makeGroup = (labelText) => {
            const group = document.createElement('label');
            group.className = 'tracker-filter-group';
            const span = document.createElement('span');
            span.textContent = labelText;
            group.appendChild(span);
            return { group };
        };

        // Dataset-specific first filter
        if (this.datasetMode === 'cement') {
            // Type select (cement)
            const { group } = makeGroup('Type');
            this.typeSelect = document.createElement('select');
            this.typeSelect.className = 'tracker-select';
            [
                { value: 'both', label: 'Both' },
                { value: 'cc', label: 'CC Projects' },
                { value: 'kilns', label: 'Calcination Kilns' },
            ].forEach((optData) => {
                const opt = document.createElement('option');
                opt.value = optData.value;
                opt.textContent = optData.label;
                this.typeSelect.appendChild(opt);
            });
            this.typeSelect.value = this.activeFilters.type;
            this.typeSelect.addEventListener('change', () => {
                this.activeFilters.type = this.typeSelect.value;
                this.applyFilters();
            });
            group.appendChild(this.typeSelect);
            this.filtersBar.appendChild(group);
        }

        // Technology select
        {
            const { group } = makeGroup('Technology');
            this.techSelect = document.createElement('select');
            this.techSelect.className = 'tracker-select';
            const techOptions = ['All', ...Array.from(this.availableTechnologies).sort((a, b) => String(a).localeCompare(String(b)))];
            techOptions.forEach((t, i) => {
                const opt = document.createElement('option');
                opt.value = i === 0 ? 'all' : String(t);
                opt.textContent = i === 0 ? 'All technologies' : String(t);
                this.techSelect.appendChild(opt);
            });
            this.techSelect.value = 'all';
            this.techSelect.addEventListener('change', () => {
                this.activeFilters.technology = this.techSelect.value;
                this.applyFilters();
            });
            group.appendChild(this.techSelect);
            this.filtersBar.appendChild(group);
        }

        // Scale select
        {
            const { group } = makeGroup('Project scale');
            this.scaleSelect = document.createElement('select');
            this.scaleSelect.className = 'tracker-select';
            const scaleOptions = ['All', ...Array.from(this.availableScales).sort((a, b) => String(a).localeCompare(String(b)))];
            scaleOptions.forEach((s, i) => {
                const opt = document.createElement('option');
                opt.value = i === 0 ? 'all' : String(s);
                opt.textContent = i === 0 ? 'All scales' : String(s);
                this.scaleSelect.appendChild(opt);
            });
            this.scaleSelect.value = 'all';
            this.scaleSelect.addEventListener('change', () => {
                this.activeFilters.scale = this.scaleSelect.value;
                this.applyFilters();
            });
            group.appendChild(this.scaleSelect);
            this.filtersBar.appendChild(group);
        }

        // Project status select (cement only)
        if (this.datasetMode === 'cement') {
            const { group } = makeGroup('Project status');
            this.statusSelect = document.createElement('select');
            this.statusSelect.className = 'tracker-select';
            const statusOptions = ['All', ...Array.from(this.availableStatuses).sort((a, b) => String(a).localeCompare(String(b)))];
            statusOptions.forEach((s, i) => {
                const opt = document.createElement('option');
                opt.value = i === 0 ? 'all' : String(s);
                opt.textContent = i === 0 ? 'All statuses' : String(s);
                this.statusSelect.appendChild(opt);
            });
            this.statusSelect.value = 'all';
            this.statusSelect.addEventListener('change', () => {
                this.activeFilters.status = this.statusSelect.value;
                this.applyFilters();
            });
            group.appendChild(this.statusSelect);
            this.filtersBar.appendChild(group);
        }

        // Insert above the map
        this.leftCol.insertBefore(this.filtersBar, this.mapWrap);
    }

    applyFilters() {
        // Recompute filtered projects and counts based on active filters
        this.projectsByIso = new Map();
        this.counts = new Map();
        const scaleFilter = this.activeFilters.scale;
        const techFilter = this.activeFilters.technology;
        const typeFilter = this.activeFilters.type || 'both';
        const statusFilter = this.activeFilters.status || 'all';
        const matches = (project) => {
            const matchesScale = scaleFilter === 'all' || String(project.scale || '').trim() === scaleFilter;
            const matchesTech = techFilter === 'all' || String(project.technology || '').trim() === techFilter;
            const matchesType = this.datasetMode === 'cement' ? (typeFilter === 'both' || (project.typeNormalized || '') === typeFilter) : true;
            const matchesStatus = this.datasetMode === 'cement' ? (statusFilter === 'all' || String(project.status || '').trim() === statusFilter) : true;
            return matchesScale && matchesTech && matchesType && matchesStatus;
        };

        for (const [iso2, list] of this.allProjectsByIso.entries()) {
            const filtered = list.filter(matches);
            if (filtered.length) this.projectsByIso.set(iso2, filtered);
            this.counts.set(iso2, filtered.length);
        }

        // Mark base map as dirty since colors changed
        this.baseMapDirty = true;

        this.buildLegend();
        this.render();
        this.renderSidebar();
        this.updateAnnouncementChart();
    }

    /**
     * Update announcement chart with all project data (unfiltered)
     */
    updateAnnouncementChart() {
        if (!this.announcementChart) return;
        // Collect all projects from all countries (ignoring filters)
        const allProjects = [];
        for (const projects of this.allProjectsByIso.values()) {
            allProjects.push(...projects);
        }
        this.announcementChart.update(allProjects);
    }

    getColorScale() {
        // Build positive-value colors using config
        const minRgb = hexToRgb(CONFIG.LEGEND_COLORS.MIN_COLOR);
        const maxRgb = hexToRgb(CONFIG.LEGEND_COLORS.MAX_COLOR);
        const buckets = 6; // positive buckets only
        const palette = Array.from({ length: buckets }, (_, i) => {
            const t = i / (buckets - 1);
            return rgbToCss(interpolateRgb(minRgb, maxRgb, t));
        });
        const stroke = CONFIG.LEGEND_COLORS.STROKE_COLOR;
        const zero = CONFIG.LEGEND_COLORS.ZERO_COLOR;

        const values = [...this.counts.values()].filter((v) => v > 0);
        const max = values.length ? Math.max(...values) : 0;
        const min = values.length ? Math.min(...values) : 0; // min of positive values (>=1)
        const positiveBuckets = Math.max(1, buckets);
        const step = max > 0 ? Math.max(1, Math.ceil((max - min + 1) / positiveBuckets)) : 1;

        const colorFor = (val) => {
            if (!val || val <= 0) return zero;
            const baseIdx = Math.floor((val - min) / step);
            const idx = Math.min(palette.length - 1, Math.max(0, baseIdx));
            return palette[idx];
        };

        return { colorFor, min, max, stroke, palette, step, zero };
    }

    render() {
        const startTime = performance.now();
        const ctx = this.ctx;

        // Clear entire canvas in device pixels
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        ctx.restore();

        // Use cached base map if available
        if (CONFIG.CANVAS_CACHE_ENABLED && this.baseMapCanvas && !this.baseMapDirty) {
            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.drawImage(this.baseMapCanvas, 0, 0);
            ctx.restore();
        } else {
            // Render base map and cache it
            this.renderBaseMapToCache();
            if (this.baseMapCanvas) {
                ctx.save();
                ctx.setTransform(1, 0, 0, 1, 0, 0);
                ctx.drawImage(this.baseMapCanvas, 0, 0);
                ctx.restore();
            } else {
                // Fallback: render directly
                this.renderBaseMapDirect(ctx);
            }
        }

        // Range hover highlight (when hovering legend swatch)
        if (this.hoveredRange) {
            const { lower, upper } = this.hoveredRange;
            const { stroke } = this.getColorScale();

            // First, gray out all countries NOT in the range (using zero-color)
            ctx.fillStyle = CONFIG.LEGEND_COLORS.ZERO_COLOR;
            ctx.strokeStyle = stroke;
            ctx.lineWidth = 1.0;
            this.isoToPaths.forEach((paths, iso2) => {
                const count = this.counts.get(iso2) || 0;
                if (count < lower || count > upper) {
                    for (const p of paths) {
                        ctx.beginPath();
                        ctx.fill(p);
                        ctx.stroke(p);
                    }
                }
            });

            // Then highlight countries in the range
            ctx.strokeStyle = 'rgba(255,255,255,0.6)';
            ctx.lineWidth = 2.5;
            ctx.fillStyle = 'rgba(255,255,255,0.05)';

            this.counts.forEach((count, iso2) => {
                if (count >= lower && count <= upper && this.isoToPaths.has(iso2)) {
                    for (const p of this.isoToPaths.get(iso2)) {
                        ctx.beginPath();
                        ctx.fill(p);
                        ctx.stroke(p);
                    }
                }
            });
        }

        // Single country hover highlight on top (always render fresh)
        if (this.hoverIso && this.isoToPaths.has(this.hoverIso)) {
            ctx.strokeStyle = 'rgba(255,255,255,0.9)';
            ctx.lineWidth = 2.5;
            ctx.fillStyle = 'rgba(255,255,255,0.08)';
            for (const p of this.isoToPaths.get(this.hoverIso)) {
                ctx.beginPath();
                ctx.fill(p);
                ctx.stroke(p);
            }
        }

        // After rendering, update sidebar height in case canvas size changed
        this.updateSidebarHeight(Math.round(this.canvas.height / (window.devicePixelRatio || 1)));

        // Performance tracking
        const renderTime = performance.now() - startTime;
        this.updatePerformanceMetrics(renderTime);
    }

    /**
     * Fallback method to render base map directly
     */
    renderBaseMapDirect(ctx) {
        // Solid white background
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        ctx.restore();

        const { colorFor, stroke } = this.getColorScale();

        // Draw countries
        this.isoToPaths.forEach((paths, iso2) => {
            const val = this.counts.get(iso2) || 0;
            const fill = colorFor(val);
            ctx.fillStyle = fill;
            ctx.strokeStyle = stroke;
            ctx.lineWidth = 1.0;
            for (const p of paths) {
                ctx.beginPath();
                ctx.fill(p);
                ctx.stroke(p);
            }
        });
    }

    /**
     * Update performance metrics
     */
    updatePerformanceMetrics(renderTime) {
        this.performanceMetrics.renderCount++;
        this.performanceMetrics.lastRenderTime = renderTime;

        // Calculate rolling average
        const count = this.performanceMetrics.renderCount;
        const prevAvg = this.performanceMetrics.avgRenderTime;
        this.performanceMetrics.avgRenderTime = (prevAvg * (count - 1) + renderTime) / count;

        // Log performance in development
        if (count % 100 === 0 && window.location.hostname === 'localhost') {
            console.log(`Render performance: ${renderTime.toFixed(2)}ms (avg: ${this.performanceMetrics.avgRenderTime.toFixed(2)}ms)`);
        }
    }

    onMouseMove(e) {
        const rect = this.canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        const px = (e.clientX - rect.left) * dpr; // canvas pixel coords
        const py = (e.clientY - rect.top) * dpr;
        // Convert to SVG viewBox space (accounting for translation by viewBox origin)
        const x = px / this.scaleX + this.viewBox.x;
        const y = py / this.scaleY + this.viewBox.y;

        // For hit-testing, use identity transform so Path2D and (x,y) are in viewBox coordinates
        this.ctx.save();
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);

        const ctx = this.ctx;
        let foundIso = null;
        // Simple per-path hit test; acceptable for ~200 countries
        for (const [iso2, paths] of this.isoToPaths.entries()) {
            for (const p of paths) {
                if (ctx.isPointInPath(p, x, y)) {
                    foundIso = iso2;
                    break;
                }
            }
            if (foundIso) break;
        }

        this.ctx.restore();
        const count = foundIso ? (this.counts.get(foundIso) || 0) : 0;
        this.hoverIso = foundIso && count > 0 ? foundIso : null;
        this.canvas.classList.toggle('cursor-pointer', Boolean(foundIso && count > 0));
        this.render();

        if (this.hoverIso) {
            const displayName = (this.iso2ToName && this.iso2ToName.get(this.hoverIso)) || this.hoverIso;
            this.showTooltip(e.clientX - rect.left, e.clientY - rect.top, `${displayName}: ${count}`);
        } else {
            this.hideTooltip();
        }
    }

    onClick(e) {
        const rect = this.canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        const px = (e.clientX - rect.left) * dpr;
        const py = (e.clientY - rect.top) * dpr;
        const x = px / this.scaleX + this.viewBox.x;
        const y = py / this.scaleY + this.viewBox.y;

        this.ctx.save();
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        let foundIso = null;
        for (const [iso2, paths] of this.isoToPaths.entries()) {
            for (const p of paths) {
                if (this.ctx.isPointInPath(p, x, y)) {
                    foundIso = iso2;
                    break;
                }
            }
            if (foundIso) break;
        }
        this.ctx.restore();

        const count = foundIso ? (this.counts.get(foundIso) || 0) : 0;
        if (!foundIso || count <= 0) return; // not clickable for 0-project countries

        this.selectedIso = foundIso;
        this.renderSidebar();
    }

    renderSidebar() {
        if (!this.sidebar) return;
        this.sidebar.innerHTML = '';
        const title = document.createElement('h3');
        title.className = 'text-sm font-semibold mt-1 mb-2 text-white';
        if (!this.selectedIso) {
            // List countries with projects under current filters
            const countries = Array.from(this.counts.entries())
                .filter(([, c]) => c > 0)
                .map(([iso, c]) => ({ iso, count: c, name: (this.iso2ToName && this.iso2ToName.get(iso)) || iso }));

            if (!countries.length) {
                title.textContent = 'No countries with projects under current filters';
                this.sidebar.appendChild(title);
                return;
            }

            title.textContent = `Countries with projects — ${countries.length}`;
            this.sidebar.appendChild(title);

            const list = document.createElement('div');
            list.className = 'tracker-country-list';

            // Find max count for bar width calculation
            const maxCount = Math.max(...countries.map(c => c.count));
            const { colorFor } = this.getColorScale();

            countries
                .sort((a, b) => b.count - a.count || String(a.name).localeCompare(String(b.name)))
                .forEach((item) => {
                    const btn = document.createElement('button');
                    btn.type = 'button';
                    btn.className = 'tracker-country-btn';

                    // Calculate bar width as percentage of max
                    const barWidthPercent = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                    const barColor = colorFor(item.count);

                    // Set CSS custom properties for the bar
                    btn.style.setProperty('--bar-width', `${barWidthPercent}%`);
                    btn.style.setProperty('--bar-color', barColor);

                    const nameSpan = document.createElement('span');
                    nameSpan.textContent = item.name;
                    const countBadge = document.createElement('span');
                    countBadge.textContent = String(item.count);
                    countBadge.className = 'tracker-badge';
                    btn.appendChild(nameSpan);
                    btn.appendChild(countBadge);
                    btn.addEventListener('click', () => {
                        this.selectedIso = item.iso;
                        this.renderSidebar();
                    });
                    list.appendChild(btn);
                });
            this.sidebar.appendChild(list);
            return;
        }
        const iso = this.selectedIso;
        const name = (this.iso2ToName && this.iso2ToName.get(iso)) || iso;
        const projects = this.projectsByIso.get(iso) || [];
        title.textContent = `${name} — ${projects.length} project${projects.length === 1 ? '' : 's'}`;
        // Close button (clear selection)
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.setAttribute('aria-label', 'Clear selection');
        closeBtn.className = 'tracker-close-btn';
        closeBtn.textContent = '✕';
        closeBtn.addEventListener('click', () => {
            this.selectedIso = null;
            this.renderSidebar();
        });
        this.sidebar.appendChild(closeBtn);
        this.sidebar.appendChild(title);

        if (!projects.length) {
            const empty = document.createElement('div');
            empty.textContent = 'No projects found in dataset';
            empty.className = 'opacity-80';
            this.sidebar.appendChild(empty);
            return;
        }

        const list = document.createElement('div');
        list.className = 'flex flex-col gap-2';
        projects
            .sort((a, b) => String(a.projectName).localeCompare(String(b.projectName)))
            .forEach((p) => {
                const card = document.createElement('div');
                card.className = 'tracker-project-card';

                const header = document.createElement('button');
                header.type = 'button';
                header.setAttribute('aria-expanded', 'false');
                header.className = 'tracker-project-header';
                header.textContent = p.projectName || 'Untitled project';

                const caret = document.createElement('span');
                caret.textContent = '▸';
                caret.className = 'ml-2';
                header.appendChild(caret);

                const body = document.createElement('div');
                body.className = 'tracker-project-body hidden';

                const row = (label, value) => {
                    if (!value) return;
                    const r = document.createElement('div');
                    const lb = document.createElement('span');
                    lb.textContent = `${label}: `;
                    lb.className = 'opacity-80';
                    const val = document.createElement('span');
                    val.textContent = value;
                    r.appendChild(lb);
                    r.appendChild(val);
                    body.appendChild(r);
                };
                row('Technology', p.technology);
                row('Project scale', p.scale);
                if (this.datasetMode === 'cement') {
                    row('Project status', p.status);
                }
                row('Company', p.company);
                if (p.memberCompanies && p.memberCompanies.length) {
                    row('LeadIT members', p.memberCompanies.join(', '));
                }

                header.addEventListener('click', () => {
                    const expanded = header.getAttribute('aria-expanded') === 'true';
                    header.setAttribute('aria-expanded', String(!expanded));
                    body.classList.toggle('hidden', expanded);
                    caret.textContent = expanded ? '▸' : '▾';
                });

                card.appendChild(header);
                card.appendChild(body);
                list.appendChild(card);
            });
        this.sidebar.appendChild(list);
    }

    extractMemberCompanies(companyField) {
        if (!companyField) return [];
        const text = String(companyField);
        const candidates = text
            .split(/[;&|,/]/)
            .map((s) => s.trim())
            .filter(Boolean);
        const members = new Set();
        const norm = (s) => s.toLowerCase();
        const memberSet = new Set((this.memberNames || []).map(norm));
        for (const name of candidates) {
            const n = norm(name);
            // direct match
            if (memberSet.has(n)) {
                members.add(name);
                continue;
            }
            // partial match when company text contains member name
            for (const m of memberSet) {
                if (n.includes(m)) {
                    members.add(name);
                    break;
                }
            }
        }
        return Array.from(members);
    }

    buildLegend() {
        this.legend.innerHTML = '';
        const { palette, min, max, step, zero } = this.getColorScale();
        if (!palette.length) return;
        const label = document.createElement('span');
        label.textContent = 'Projects:';
        this.legend.appendChild(label);
        // Zero swatch (inline styles as before)
        {
            const sw0 = document.createElement('span');
            sw0.style.display = 'inline-block';
            sw0.style.width = '16px';
            sw0.style.height = '12px';
            sw0.style.background = zero;
            sw0.style.border = '1px solid rgba(0,0,0,0.2)';
            sw0.title = '0';
            this.legend.appendChild(sw0);
        }
        // Positive ranges starting at 1 (inline styles as before)
        palette.forEach((color, i) => {
            const sw = document.createElement('span');
            sw.style.display = 'inline-block';
            sw.style.width = '16px';
            sw.style.height = '12px';
            sw.style.background = color;
            sw.style.border = '1px solid rgba(0,0,0,0.2)';
            sw.style.cursor = 'pointer';
            const lower = Math.max(1, min + i * step);
            const upper = Math.min(max, min + (i + 1) * step - 1);
            sw.title = `${lower}–${upper}`;
            sw.dataset.lower = lower;
            sw.dataset.upper = upper;

            // Add hover event listeners
            sw.addEventListener('mouseenter', (e) => {
                this.hoveredRange = { lower, upper };
                this.render();

                // Count countries in this range
                let countryCount = 0;
                this.counts.forEach((count) => {
                    if (count >= lower && count <= upper) {
                        countryCount++;
                    }
                });

                // Show tooltip with range and country count
                const rect = this.canvas.getBoundingClientRect();
                const swRect = sw.getBoundingClientRect();
                const tooltipX = swRect.left - rect.left + swRect.width / 2;
                const tooltipY = swRect.top - rect.top;
                this.showTooltip(tooltipX, tooltipY, `${lower}–${upper} projects: ${countryCount} ${countryCount === 1 ? 'country' : 'countries'}`);
            });
            sw.addEventListener('mouseleave', () => {
                this.hoveredRange = null;
                this.hideTooltip();
                this.render();
            });

            this.legend.appendChild(sw);
        });
        const range = document.createElement('span');
        range.textContent = ` 0–${max}`;
        this.legend.appendChild(range);
    }

    showTooltip(cx, cy, text) {
        this.tooltip.textContent = text;
        this.tooltip.style.left = `${cx}px`;
        this.tooltip.style.top = `${cy}px`;
        this.tooltip.classList.add('tracker-tooltip--visible');
    }

    hideTooltip() {
        this.tooltip.classList.remove('tracker-tooltip--visible');
    }
}

// Utils
function hexToRgb(hex) {
    const n = hex.replace('#', '');
    const bigint = parseInt(n, 16);
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}
function rgbToCss({ r, g, b }) {
    return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}
function interpolateRgb(a, b, t) {
    return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t };
}
function capitalize(str) {
    return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;
}

function normalizeName(name) {
    if (!name) return '';
    return String(name)
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '') // strip diacritics
        .replace(/[^a-z\s\-']/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function normalizeHeaderName(h) {
    const s = normalizeName(h);
    if (!s) return '';
    // Map common headers to simpler keys
    if (/(^|\b)internal id(\b|$)/.test(s)) return 'internal_id';
    if (/(^|\b)project name(\b|$)/.test(s)) return 'project_name';
    if (/(^|\b)country(\b|$)/.test(s)) return 'country';
    if (/(^|\b)location(\b|$)/.test(s)) return 'location';
    if (/(^|\b)iso2?(\b|$)/.test(s)) return 'iso2';
    if (/(^|\b)code(\b|$)/.test(s)) return 'code';
    return s.replace(/\s+/g, '_');
}

/**
 * Parse announcement date and extract year
 * Handles formats: yyyy-mm-dd, yyyy/mm/dd, Excel serial dates, and plain year numbers
 * @param {string|number} dateValue - Raw date value from spreadsheet
 * @returns {number|null} - Year as number or null if invalid
 */
function parseAnnouncementYear(dateValue) {
    if (!dateValue) return null;
    const val = String(dateValue).trim();
    if (!val) return null;

    // Try yyyy-mm-dd or yyyy/mm/dd format
    const isoMatch = val.match(/^(\d{4})[-/]\d{1,2}[-/]\d{1,2}/);
    if (isoMatch) {
        const year = parseInt(isoMatch[1], 10);
        if (year >= 1990 && year <= 2100) return year;
    }

    // Try plain 4-digit year
    const yearMatch = val.match(/^(\d{4})$/);
    if (yearMatch) {
        const year = parseInt(yearMatch[1], 10);
        if (year >= 1990 && year <= 2100) return year;
    }

    // Handle Excel serial date numbers (days since 1900-01-01)
    const numVal = parseFloat(val);
    if (!isNaN(numVal) && numVal > 30000 && numVal < 60000) {
        // Excel serial date: convert to JS Date
        const excelEpoch = new Date(1899, 11, 30); // Excel epoch
        const date = new Date(excelEpoch.getTime() + numVal * 86400000);
        const year = date.getFullYear();
        if (year >= 1990 && year <= 2100) return year;
    }

    return null;
}


